import React, { useState, useEffect } from 'react';
import { AttachMoney, CalendarMonth, Description } from '@mui/icons-material';
import { Modal } from '@components/molecules/Modal';
import { Input } from '@components/atoms/Input';
import { Card } from '@components/atoms/Card';
import { SmallDocumentList } from '@components/molecules/SmallDocumentList';
import { FileDropZone } from '@components/molecules/FileDropZone';
import {
  BudgetCounterProposalCreateInput,
  BudgetProposal,
} from '@models/budget.model';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { format } from 'date-fns';

interface CounterProposalModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: BudgetCounterProposalCreateInput) => void;
  originalProposal: BudgetProposal;
  isSubmitting: boolean;
}

const validationSchema = Yup.object({
  proposedStartDate: Yup.date()
    .required('Start date is required')
    .typeError('Invalid date'),
  proposedEndDate: Yup.date()
    .required('End date is required')
    .min(Yup.ref('proposedStartDate'), 'End date must be after start date')
    .typeError('Invalid date'),
  // Sprint 23, story-110 (QVIKLDSG). Same boundary/message as BudgetResponseModal's schema — see
  // docs/ux/story-110-design.md and that component's comment for the full rationale.
  proposedAmount: Yup.number()
    .positive('Amount must be positive')
    .required('Proposed amount is required')
    .max(99_999_999.99, "Bid amounts over $99,999,999.99 aren't supported. Enter an amount of $99,999,999.99 or less.")
    .typeError('Invalid amount'),
  description: Yup.string(),
});

const CounterProposalModal: React.FC<CounterProposalModalProps> = ({
  open,
  onClose,
  onSubmit,
  originalProposal,
  isSubmitting,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const formik = useFormik({
    initialValues: {
      proposedStartDate: format(
        new Date(originalProposal.proposedStartDate),
        'yyyy-MM-dd',
      ),
      proposedEndDate: format(
        new Date(originalProposal.proposedEndDate),
        'yyyy-MM-dd',
      ),
      proposedAmount: originalProposal.proposedAmount?.toString() || '',
      description: '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      const input: BudgetCounterProposalCreateInput = {
        parentProposalId: originalProposal.budgetProposalId,
        proposedStartDate: values.proposedStartDate,
        proposedEndDate: values.proposedEndDate,
        proposedAmount: parseFloat(values.proposedAmount),
        description: values.description || undefined,
        files: selectedFiles.length > 0 ? selectedFiles : undefined,
      };

      if (selectedFiles.length > 0) {
        setIsUploading(true);
      }

      try {
        onSubmit(input);
      } finally {
        setIsUploading(false);
      }
    },
  });

  // Inject z-index override to ensure counter proposal modal appears above proposal history slider
  useEffect(() => {
    if (open) {
      const styleId = 'counter-proposal-modal-z-index-override';
      let style = document.getElementById(styleId) as HTMLStyleElement;

      if (!style) {
        style = document.createElement('style');
        style.id = styleId;
        document.head.appendChild(style);
      }

      // Use extremely high z-index values to ensure counter proposal modal appears above everything
      // Counter proposal needs to be above proposal history slider (99999/100000)
      style.textContent = `
        /* Override z-index for counter proposal modal - target ReactModal elements */
        .ReactModal__Overlay:has(.counter-proposal-modal-wrapper) {
          z-index: 199999 !important;
        }
        .ReactModal__Content:has(.counter-proposal-modal-wrapper) {
          z-index: 200000 !important;
        }
      `;

      return () => {
        if (style && style.parentNode) {
          style.parentNode.removeChild(style);
        }
      };
    }
  }, [open]);

  const handleClose = () => {
    if (isUploading) return; // Prevent closing during upload
    formik.resetForm();
    setSelectedFiles([]);
    onClose();
  };

  const formatCurrency = (amount?: number): string => {
    if (!amount) return 'Not specified';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <Modal
      isOpen={open}
      onClose={handleClose}
      size="large"
      title="Make Counter Proposal"
      showCloseButton={true}
      className="max-h-[90vh]"
      handleConfirm={() => formik.handleSubmit()}
      confirmText={
        isSubmitting
          ? 'Submitting...'
          : isUploading
            ? 'Uploading...'
            : 'Submit Counter Proposal'
      }
      disableConfirm={
        isSubmitting || isUploading || !formik.isValid || !formik.dirty
      }
      isLoading={isSubmitting || isUploading}>
      <div className="pt-5 counter-proposal-modal-wrapper">
        {/* Original Proposal Reference */}
        <Card className="p-4 mb-6 bg-slate-50 border border-slate-300">
          <h4 className="text-sm font-semibold text-slate-700 mb-4">
            Original Proposal from{' '}
            {originalProposal.proposingOrganization?.name}
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
            <div>
              <p className="text-xs text-gray-500 mb-1">Amount</p>
              <p className="text-sm font-medium text-gray-900">
                {formatCurrency(originalProposal.proposedAmount)}
              </p>
            </div>

            <div>
              <p className="text-xs text-gray-500 mb-1">Start Date</p>
              <p className="text-sm font-medium text-gray-900">
                {format(
                  new Date(originalProposal.proposedStartDate),
                  'MMM dd, yyyy',
                )}
              </p>
            </div>

            <div>
              <p className="text-xs text-gray-500 mb-1">End Date</p>
              <p className="text-sm font-medium text-gray-900">
                {format(
                  new Date(originalProposal.proposedEndDate),
                  'MMM dd, yyyy',
                )}
              </p>
            </div>
          </div>

          {originalProposal.description && (
            <div className="mb-3">
              <p className="text-xs text-gray-500 mb-1">Description</p>
              <p className="text-sm text-gray-600">
                {originalProposal.description}
              </p>
            </div>
          )}

          {/* Documents from Original Proposal */}
          {originalProposal.documents && originalProposal.documents.length > 0 && (
            <div>
              <p className="text-xs text-gray-500 mb-2">Attached Documents</p>
              <SmallDocumentList
                documents={originalProposal.documents}
                showDelete={false}
              />
            </div>
          )}
        </Card>

        <div className="relative flex items-center mb-6">
          <div className="flex-grow border-t border-gray-300"></div>
          <span className="flex-shrink mx-4 text-xs text-gray-500 bg-white px-2">
            Your Counter Proposal
          </span>
          <div className="flex-grow border-t border-gray-300"></div>
        </div>

        <form onSubmit={formik.handleSubmit}>
          <div className="space-y-6">
            {/* Proposed Amount */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                Your Proposed Amount
              </label>
              <div className="relative">
                <AttachMoney className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-600" />
                <Input
                  type="number"
                  name="proposedAmount"
                  value={formik.values.proposedAmount}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter amount..."
                  className="pl-10 w-full"
                  error={
                    formik.touched.proposedAmount &&
                    Boolean(formik.errors.proposedAmount)
                  }
                />
              </div>
              {formik.touched.proposedAmount && formik.errors.proposedAmount && (
                <p className="text-red-500 text-sm">{formik.errors.proposedAmount}</p>
              )}
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Your Proposed Start Date
                </label>
                <div className="relative">
                  <CalendarMonth className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-600" />
                  <Input
                    type="date"
                    name="proposedStartDate"
                    value={formik.values.proposedStartDate}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="pl-10 w-full"
                    error={
                      formik.touched.proposedStartDate &&
                      Boolean(formik.errors.proposedStartDate)
                    }
                  />
                </div>
                {formik.touched.proposedStartDate && formik.errors.proposedStartDate && (
                  <p className="text-red-500 text-sm">{formik.errors.proposedStartDate}</p>
                )}
              </div>
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Your Proposed End Date
                </label>
                <div className="relative">
                  <CalendarMonth className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-600" />
                  <Input
                    type="date"
                    name="proposedEndDate"
                    value={formik.values.proposedEndDate}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="pl-10 w-full"
                    error={
                      formik.touched.proposedEndDate &&
                      Boolean(formik.errors.proposedEndDate)
                    }
                  />
                </div>
                {formik.touched.proposedEndDate && formik.errors.proposedEndDate && (
                  <p className="text-red-500 text-sm">{formik.errors.proposedEndDate}</p>
                )}
              </div>
            </div>

            {/* Description */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                Counter Proposal Notes (Optional)
              </label>
              <div className="relative">
                <Description className="absolute left-3 top-3 w-5 h-5 text-blue-600" />
                <textarea
                  name="description"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  rows={4}
                  placeholder="Explain your counter proposal terms and reasoning..."
                  className={`pl-10 w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent resize-none ${
                    formik.touched.description && formik.errors.description
                      ? 'border-red-500'
                      : 'border-gray-300'
                  }`}
                />
              </div>
              {formik.touched.description && formik.errors.description && (
                <p className="text-red-500 text-sm">{formik.errors.description}</p>
              )}
            </div>

            {/* File Upload Section */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">
                Attach Documents (Optional)
              </h4>
              <FileDropZone
                files={selectedFiles}
                onFilesChange={setSelectedFiles}
                disabled={isUploading}
                isUploading={isUploading}
                activeColor="indigo"
              />
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default CounterProposalModal;
